import { KeyboardEvent } from 'react'
import { BaseRange, Editor, Node, Path, Element as SlateElement, Transforms } from 'slate'

import { BLOCK_FORMATS } from '../interface/custom-types'

import { listsNodeHelper } from './listsHelper'
import { quitNodeByDoubleEnter } from './quitNodeHelper'

interface IEnterBehaviorInLists {
  editor: Editor
  selection: BaseRange
  event: KeyboardEvent
  parentPath: Path
}

export const insertNode = (editor: Editor, node: Node, path: Path) => {
  Transforms.insertNodes(editor, node, {
    at: Path.next(path),
  })
  Transforms.select(editor, Path.next(path))
}

const enterBehaviorInLists = ({ editor, selection, event, parentPath }: IEnterBehaviorInLists) => {
  const [node] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === BLOCK_FORMATS.paragraph,
  })

  if (node) {
    Transforms.splitNodes(editor)
    const nextNodePath = Path.next(node[1])
    if (nextNodePath) {
      const hasNextNode = Node.has(editor, nextNodePath)
      if (hasNextNode) {
        const nodeForTransfer = Node.get(editor, nextNodePath)
        if (SlateElement.isElement(nodeForTransfer)) {
          const listItemNode = {
            type: BLOCK_FORMATS.listItem,
            children: [nodeForTransfer],
          }
          insertNode(editor, listItemNode, parentPath)
          Transforms.removeNodes(editor, { at: nextNodePath })
        }
      } else {
        const currentNodeString = Node.string(node[0])
        if (
          currentNodeString.length &&
          selection.anchor.offset === 0 &&
          SlateElement.isElement(node[0])
        ) {
          const listItemNode = {
            type: BLOCK_FORMATS.listItem,
            children: [node[0]],
          }
          insertNode(editor, listItemNode, parentPath)
          Transforms.removeNodes(editor, { at: node[1] })
          Transforms.insertNodes(
            editor,
            { type: BLOCK_FORMATS.paragraph, children: [{ text: '' }] },
            { at: node[1] }
          )
        } else {
          const listItemNode = {
            type: BLOCK_FORMATS.listItem,
            children: [{ type: BLOCK_FORMATS.paragraph, children: [{ text: '' }] }],
          }
          insertNode(editor, listItemNode, parentPath)
        }
      }
      Transforms.collapse(editor, { edge: 'start' })
    }

    return event.preventDefault()
  }
}

export const enterBehavior = (editor: Editor, selection: BaseRange, event: KeyboardEvent) => {
  const { isList, parentNode, parentPath } = listsNodeHelper(editor, selection)

  if (!parentNode || !parentPath) {
    return
  }

  // This check will leave the ability to exit nested nodes and at the same time maintain
  // correct behavior when moving to a new line inside a cell
  const isInTable = Editor.above(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.table,
  })
  if (isInTable) {
    const [nodeInCell] = Editor.parent(editor, parentPath)
    if (SlateElement.isElement(nodeInCell) && nodeInCell.type === BLOCK_FORMATS.tableCell) {
      return
    }
  }

  const isQuittedNode = quitNodeByDoubleEnter({ editor, event, parentPath, parentNode })

  if (isQuittedNode) {
    return
  }

  // This method implements special behavior for enter inside list items.
  // When you press enter inside the list, we must transfer the nodes that are in
  // this list element after the current selection. If the current selection is at the end
  // of the list element, then you need to create a new list element with a paragraph type node inside.
  if (isList) {
    enterBehaviorInLists({ editor, selection, event, parentPath })
  }
}
